import timesheetConflictRetry from '@/mixins/timesheetConflictRetry'

// One definition of every "finish / stop / remove this job" action, shared by
// the grid chip's ⋯ menu and the edit modal's footer menu. Both used to carry
// their own copy of the confirm-dispatch-toast code (SchedulePage.vue and
// JobFormModal.vue), which is how the two drifted into different wording for
// the same operation.
//
// Two verbs, deliberately: Cancel is reversible, Delete is permanent. Only the
// delete items are styled danger — see DESIGN.md's two-tier severity rule.
//
//   job ──► jobActionItems(job) ──► [{ key, label, danger, group }]
//                                        │
//                       runJobAction(key, job) ──► confirm ──► store dispatch
//                                        │                          │
//                                        └──────── toast ◄──────────┘
const ITEMS = [
  {
    key: 'complete',
    label: 'Mark complete',
    group: 'job',
    when: (job) => job.status === 'upcoming',
  },
  {
    key: 'cancel',
    label: 'Cancel job',
    group: 'job',
    when: (job) => job.status !== 'completed' && job.status !== 'cancelled',
  },
  {
    key: 'reopen',
    label: 'Reopen job',
    group: 'job',
    when: (job) => job.status === 'cancelled',
  },
  {
    key: 'make-repeat',
    label: 'Make this repeat…',
    group: 'job',
    when: (job) =>
      !job.recurrence_group_id &&
      job.status !== 'cancelled' &&
      job.status !== 'completed',
  },
  {
    key: 'delete',
    label: 'Delete job',
    group: 'job',
    danger: true,
    when: () => true,
  },
  {
    key: 'complete-series',
    label: 'Mark series complete',
    group: 'series',
    when: (job) => !!job.recurrence_group_id,
  },
  {
    key: 'cancel-series',
    label: 'Cancel series',
    group: 'series',
    // cancellable_count is only known where the caller has loaded the
    // series summary (JobFormModal's inline menu) - null there means "not
    // loaded yet", not "zero", so the item stays visible until the real
    // count arrives. The roster grid's chip menu never loads this, so it
    // always sees null and keeps today's behaviour.
    when: (job) =>
      !!job.recurrence_group_id &&
      (job.cancellable_count == null || job.cancellable_count > 0),
    countsKey: 'cancellable_count',
  },
  {
    key: 'reopen-series',
    label: 'Reopen series',
    group: 'series',
    when: (job) =>
      !!job.recurrence_group_id &&
      (job.reopenable_count == null || job.reopenable_count > 0),
    countsKey: 'reopenable_count',
  },
  {
    key: 'detach',
    label: 'Remove this job from the series',
    group: 'series',
    when: (job) => !!job.recurrence_group_id,
  },
  {
    key: 'delete-series',
    label: 'Delete series',
    group: 'series',
    danger: true,
    when: (job) => !!job.recurrence_group_id,
  },
]

export default {
  mixins: [timesheetConflictRetry],

  methods: {
    // Items that apply to THIS job. Inapplicable actions are absent rather
    // than disabled — a greyed row the BA can't explain is worse than a
    // shorter menu. The exception is `pending`: while the caller is still
    // fetching the series counts an item's `when()` depends on, that item is
    // kept but marked pending (rendered disabled/"Checking…") rather than
    // shown fully clickable and then yanked away once the real count lands.
    jobActionItems(job, countsLoading = false) {
      if (!job) return []
      return ITEMS.filter((item) => item.when(job)).map((item) => ({
        key: item.key,
        label: item.label,
        group: item.group,
        danger: !!item.danger,
        pending: !!(
          item.countsKey &&
          countsLoading &&
          job[item.countsKey] == null
        ),
      }))
    },

    // Resolves to { changed } — changed=false when the user backed out of a
    // confirm, so callers can skip a pointless refetch. 'make-repeat' is not
    // handled here: it's the caller's job to open the repeating form.
    async runJobAction(key, job) {
      switch (key) {
        case 'complete':
          return this._setJobStatus(job, 'completed', {
            confirm: `Mark "${job.title}" as complete?`,
            title: 'Completed',
            message: 'Job marked complete.',
            failure: 'Failed to mark job complete.',
          })
        case 'cancel':
          return this._setJobStatus(job, 'cancelled', {
            confirm:
              'Cancel this job? Workers assigned to it will see it marked as cancelled.',
            title: 'Cancelled',
            message: 'Job cancelled.',
            failure: 'Failed to cancel job.',
          })
        case 'reopen':
          return this._setJobStatus(job, 'upcoming', {
            confirm: 'Reopen this job? Workers will be notified.',
            title: 'Reopened',
            message: 'Job reopened.',
            failure: 'Failed to reopen job.',
          })
        case 'delete':
          return this._deleteJob(job)
        case 'complete-series':
          return this._seriesStatusAction(job, 'completeJobSeries', {
            confirm:
              'Mark the entire series as complete? (Today and future dates only.)',
            title: 'Completed',
            done: 'Series marked complete.',
            none: 'Nothing to update - every job in this series is already completed, cancelled, or in the past.',
            failure: 'Failed to mark series complete.',
          })
        case 'cancel-series':
          return this._seriesStatusAction(job, 'cancelJobSeries', {
            confirm:
              'Cancel the entire series? Today and future jobs in it will be marked as cancelled - workers assigned to them will be notified. This can be undone per job afterward.',
            title: 'Cancelled',
            done: (data) => `${data.updated} job(s) in the series cancelled.`,
            none: 'No upcoming jobs left in this series to cancel.',
            failure: 'Failed to cancel series.',
          })
        case 'reopen-series':
          return this._seriesStatusAction(job, 'reopenJobSeries', {
            confirm:
              'Reopen the entire series? Cancelled jobs in it that are today or in the future will be marked Upcoming again - workers assigned to them will be notified.',
            title: 'Reopened',
            done: (data) => `${data.updated} job(s) in the series reopened.`,
            none: 'No cancelled jobs left in this series to reopen.',
            failure: 'Failed to reopen series.',
          })
        case 'detach':
          return this._detachJob(job)
        case 'delete-series':
          return this._deleteSeries(job)
        default:
          return { changed: false }
      }
    },

    async _setJobStatus(job, status, copy) {
      if (!window.confirm(copy.confirm)) return { changed: false }

      try {
        await this.$store.dispatch('schedule/updateJob', { id: job.id, status })
        this._toast(copy.message, copy.title)
        return { changed: true }
      } catch (e) {
        // A past-dated reopen comes back 422 with a date error — the caller
        // opens the form so the BA can pick a new date, rather than a toast
        // they can't act on.
        const dateErrors = this._dateErrors(e)
        if (dateErrors) return { changed: false, dateError: dateErrors[0] }

        this._toast(copy.failure, 'Error', 'danger')
        return { changed: false }
      }
    },

    async _seriesStatusAction(job, action, copy) {
      if (!window.confirm(copy.confirm)) return { changed: false }

      try {
        const data = await this.$store.dispatch(
          `schedule/${action}`,
          job.recurrence_group_id,
        )
        if (data.updated > 0) {
          const message =
            typeof copy.done === 'function' ? copy.done(data) : copy.done
          this._toast(message, copy.title)
          return { changed: true }
        }
        this._toast(copy.none, 'No changes made', 'warning')
        return { changed: false }
      } catch (e) {
        this._toast(copy.failure, 'Error', 'danger')
        return { changed: false }
      }
    },

    async _deleteJob(job) {
      if (!window.confirm(`Delete "${job.title}"? This can't be undone.`)) {
        return { changed: false }
      }

      try {
        await this.withTimesheetConflictRetry(
          (forceArgs) =>
            this.$store.dispatch('schedule/deleteJob', {
              id: job.id,
              ...forceArgs,
            }),
          'Delete anyway',
        )
        this._toast('Job deleted.', 'Deleted')
        return { changed: true }
      } catch (e) {
        if (e && e.timesheetConflictCancelled) return { changed: false }
        this._toast('Failed to delete job.', 'Error', 'danger')
        return { changed: false }
      }
    },

    async _deleteSeries(job) {
      if (
        !window.confirm(
          'Delete the entire series? All jobs in it will be permanently deleted.',
        )
      ) {
        return { changed: false }
      }

      try {
        await this.withTimesheetConflictRetry(
          (forceArgs) =>
            this.$store.dispatch('schedule/deleteJobSeries', {
              recurrenceGroupId: job.recurrence_group_id,
              ...forceArgs,
            }),
          'Delete anyway',
        )
        this._toast('Series deleted.', 'Deleted')
        return { changed: true }
      } catch (e) {
        if (e && e.timesheetConflictCancelled) return { changed: false }
        this._toast('Failed to delete series.', 'Error', 'danger')
        return { changed: false }
      }
    },

    async _detachJob(job) {
      if (
        !window.confirm(
          'Remove this job from the recurring series? The job itself will be kept.',
        )
      ) {
        return { changed: false }
      }

      try {
        await this.$store.dispatch('schedule/detachJobFromSeries', job.id)
        this._toast('Job removed from series.', 'Removed from series')
        return { changed: true }
      } catch (e) {
        this._toast('Failed to remove job from series.', 'Error', 'danger')
        return { changed: false }
      }
    },

    _dateErrors(e) {
      return (
        e &&
        e.response &&
        e.response.status === 422 &&
        e.response.data &&
        e.response.data.errors &&
        e.response.data.errors.date
      )
    },

    _toast(message, title, variant = 'success') {
      this.$bvToast.toast(message, { variant, title })
    },
  },
}
